const API_BASE = (import.meta.env.VITE_ADMIN_API_BASE ?? "").replace(/\/$/, "");

/**
 * The operator's Basic password (and the session cookie via `credentials:
 * "include"`) must NEVER ride to an origin other than the one that served the
 * admin app. In the shipped single-origin image API_BASE is "" (same-origin).
 * A build that misconfigures VITE_ADMIN_API_BASE to a foreign origin would
 * otherwise silently POST the operator's credentials off-site on every call —
 * so we fail closed instead.
 */
function isSameOriginBase(base: string): boolean {
  if (!base) return true;
  if (typeof location === "undefined") return false;
  try {
    return new URL(base, location.origin).origin === location.origin;
  } catch {
    return false;
  }
}
const SAME_ORIGIN = isSameOriginBase(API_BASE);

// Basic creds live in sessionStorage under this key (written by auth.tsx's
// login/logout). The api client reads them itself so callers never thread creds
// through every call. The Google cookie lane leaves no creds here — the
// HttpOnly session cookie rides on `credentials: "include"` instead. Either way
// the client knows how to authenticate; pages just call `getX()`.
export const STORAGE_KEY = "quizkit_admin_creds";

/** Full URL the browser navigates to to start the Google OAuth flow. */
export function googleLoginUrl(): string {
  return `${API_BASE}/api/admin/auth/google/login`;
}

export interface Credentials {
  username: string;
  password: string;
}

/** Reads the stored Basic creds, or null under a Google cookie session. */
export function readStoredCreds(): Credentials | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as Credentials) : null;
  } catch {
    return null;
  }
}

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    public errors?: string[],
    /** A human-readable string `detail` from the backend (FastAPI's default), if any. */
    public detail?: string,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

/** A 422 from a strict-write config validator — carries the field error list. */
export class ValidationError extends ApiError {
  constructor(message: string, errors: string[]) {
    super(422, message, errors);
    this.name = "ValidationError";
  }
}

/**
 * Renders any thrown api error as a single user-facing string. Preference order:
 * a 422 {@link ValidationError}'s field messages, then a human string `detail`
 * from the backend (FastAPI's default — e.g. a duplicate-key 409), then the
 * supplied generic fallback. The synthetic `"Request failed: N"` message is
 * never shown. One place owns this rule so catch blocks stop re-implementing it.
 */
export function formatApiError(err: unknown, fallback = "Something went wrong."): string {
  if (err instanceof ApiError) {
    if (err.errors?.length) return err.errors.join("; ");
    if (err.detail) return err.detail;
  }
  return fallback;
}

export interface ResponseCount {
  answer_id: string;
  label?: string | null;
  count: number;
  pct_of_answered: number | null;
  pct_of_started?: number | null;
}
export interface FunnelStep {
  question_id: string;
  label?: string | null;
  question_type?: string | null;
  viewed: number;
  answered: number;
  exits?: number;
  drop_off_pct: number | null;
  time_on_page_ms?: number | null;
  responses: ResponseCount[];
}
export interface DailyPoint {
  day: string; // YYYY-MM-DD (UTC)
  visitors: number;
  submits: number;
  avg_duration_ms?: number | null;
}
export interface DeviceBreakdown {
  desktop: number;
  mobile: number;
  tablet: number;
  unknown: number;
}
export type Cohort = "all" | "completers" | "abandoners";
export interface FunnelReport {
  quiz_slug: string;
  started: number;
  completed: number;
  completion_rate: number | null;
  results_viewed?: number;
  cta_clicks?: number;
  avg_duration_ms?: number | null;
  submissions: number;
  steps: FunnelStep[];
  persona_counts: Record<string, number>;
  daily?: DailyPoint[];
  devices?: DeviceBreakdown;
}
export interface FunnelQuery {
  persona?: string;
  /** Inclusive ISO datetime lower bound. */
  date_from?: string;
  /** EXCLUSIVE ISO datetime upper bound. */
  date_to?: string;
  cohort?: Cohort;
}
export interface AdminSubmission {
  id: number;
  quiz_slug: string;
  persona: string | null;
  email: string | null;
  answers: Record<string, unknown>;
  session_id: string | null;
  created_at: string;
}
export interface SubmissionList {
  total: number;
  page: number;
  page_size: number;
  submissions: AdminSubmission[];
}
export interface Tenant {
  key: string;
  name: string;
}
export interface TenantList {
  tenants: Tenant[];
}
export interface Me {
  username: string;
  role: "operator" | "tenant_admin";
  tenant_key: string | null;
}
export interface AuthConfig {
  google: boolean;
}
export interface LeadDestinationSafe {
  type: string;
  url: string;
  has_secret: boolean;
}
export interface TenantDetail {
  key: string;
  name: string;
  branding: Record<string, unknown> | null;
  lead_destination: LeadDestinationSafe | null;
  created_at: string;
}
export interface TenantCreate {
  key: string;
  name: string;
  branding?: Record<string, unknown> | null;
  lead_destination?: Record<string, unknown> | null;
}
export interface TenantUpdate {
  name?: string;
  branding?: Record<string, unknown> | null;
  lead_destination?: Record<string, unknown> | null;
}
export interface QuizInfo {
  slug: string;
  personas: string[];
}
export interface QuizList {
  quizzes: QuizInfo[];
}

function normalizeHeaders(h: HeadersInit | undefined): Record<string, string> {
  if (!h) return {};
  if (h instanceof Headers) return Object.fromEntries(h.entries());
  if (Array.isArray(h)) return Object.fromEntries(h);
  return { ...h };
}

/**
 * The single fetch seam. Reads Basic creds from sessionStorage (or accepts an
 * explicit override for the pre-store login probe), always sends the cookie via
 * `credentials: "include"`, and maps a non-ok response to a typed error — a
 * {@link ValidationError} for 422-with-errors, else a plain {@link ApiError}.
 */
async function request<T>(
  path: string,
  init?: RequestInit,
  credsOverride?: Credentials | null,
): Promise<T> {
  if (!SAME_ORIGIN) {
    // Fail closed: never attach credentials to a cross-origin API base.
    throw new ApiError(0, "Refusing to send admin credentials cross-origin");
  }
  const creds = credsOverride === undefined ? readStoredCreds() : credsOverride;
  const headers: Record<string, string> = normalizeHeaders(init?.headers);
  if (creds) headers.Authorization = `Basic ${btoa(`${creds.username}:${creds.password}`)}`;
  if (init?.body) headers["Content-Type"] = "application/json";
  const resp = await fetch(`${API_BASE}${path}`, {
    ...init,
    credentials: "include",
    headers,
  });
  if (!resp.ok) {
    let errors: string[] | undefined;
    let detail: string | undefined;
    try {
      const b = (await resp.clone().json()) as { detail?: { errors?: string[] } | string };
      if (b?.detail && typeof b.detail === "object" && Array.isArray(b.detail.errors)) {
        errors = b.detail.errors;
      } else if (typeof b?.detail === "string") {
        detail = b.detail; // FastAPI's default string detail (e.g. a 409 reason)
      }
    } catch {
      /* non-JSON body */
    }
    if (resp.status === 422 && errors?.length) {
      throw new ValidationError(`Request failed: ${resp.status}`, errors);
    }
    throw new ApiError(resp.status, `Request failed: ${resp.status}`, errors, detail);
  }
  if (resp.status === 204) return undefined as T;
  return (await resp.json()) as T;
}

/**
 * Probes /me. Accepts an explicit creds override so the login form can verify a
 * username/password BEFORE storing it; all other callers omit it and ride the
 * stored creds or the cookie.
 */
export function getMe(credsOverride?: Credentials | null): Promise<Me> {
  return request("/api/admin/me", undefined, credsOverride);
}

export function getAuthConfig(): Promise<AuthConfig> {
  return request("/api/admin/auth/config", undefined, null);
}

// CSRF note: a cross-site POST here can at worst force-signout (no data loss),
// and SameSite=Lax already withholds the session cookie cross-site. Add a CSRF
// token if the backend ever enforces one.
export function logoutRequest(): Promise<void> {
  return request("/api/admin/auth/logout", { method: "POST" }, null);
}

export function getTenants(): Promise<TenantList> {
  return request("/api/admin/tenants");
}

export function getQuizzes(tenant: string): Promise<QuizList> {
  return request(`/api/admin/quizzes?tenant=${encodeURIComponent(tenant)}`);
}

export function getFunnel(tenant: string, slug: string, query?: FunnelQuery): Promise<FunnelReport> {
  const params = new URLSearchParams({ tenant, slug });
  if (query?.persona) params.set("persona", query.persona);
  if (query?.date_from) params.set("date_from", query.date_from);
  if (query?.date_to) params.set("date_to", query.date_to);
  if (query?.cohort && query.cohort !== "all") params.set("cohort", query.cohort);
  return request(`/api/admin/analytics/funnel?${params.toString()}`);
}

export function getSubmissions(opts: { tenant: string; slug?: string; page?: number }): Promise<SubmissionList> {
  const params = new URLSearchParams({ tenant: opts.tenant });
  if (opts.slug) params.set("slug", opts.slug);
  if (opts.page) params.set("page", String(opts.page));
  return request(`/api/admin/submissions?${params.toString()}`);
}

export function getTenant(key: string): Promise<TenantDetail> {
  return request(`/api/admin/tenants/${encodeURIComponent(key)}`);
}
export function createTenant(body: TenantCreate): Promise<TenantDetail> {
  return request("/api/admin/tenants", { method: "POST", body: JSON.stringify(body) });
}
export function updateTenant(key: string, body: TenantUpdate): Promise<TenantDetail> {
  return request(`/api/admin/tenants/${encodeURIComponent(key)}`, { method: "PATCH", body: JSON.stringify(body) });
}
export function deleteTenant(key: string): Promise<void> {
  return request(`/api/admin/tenants/${encodeURIComponent(key)}`, { method: "DELETE" });
}
